import { DateRangeValueWithStart } from "@/components/interactive/SharedTimeRange.types";
import { offlineQueryStatusTextProto } from "@/components/query/QueryStatusIcon";
import { JobQueueState } from "@/gen_src/proto/chalk/server/v1/dataplanejobqueue_pb";
import {
  BatchOpStatus,
  BatchProgress,
  BatchReport,
  OfflineQueryKind,
  OfflineQueryMeta,
  OfflineQueryShard,
  OfflineQueryStatus,
} from "@/gen_src/proto/chalk/server/v1/offline_queries_pb";
import { Datetime } from "@/scalars";
import { compileOnlyAssertNever } from "@/utils/asserts";
import { maybeProtoValueToJson, timestampToDate } from "@/utils/proto-utils";
import { fqnIsChalkInternal, pathToFirstElement } from "@/utils/resolver-utils";
import { uniq } from "lodash";

export interface OfflineQueryFiltersBranch {
  branch_id?: string;
  min_inserted_at?: string;
  max_inserted_at?: string;
}

export type ObservedAtBound = Datetime | number;

export interface OfflineQueryFilters {
  branches?: OfflineQueryFiltersBranch[];
  max_cache_age_secs?: number;
  max_samples?: number;
  observed_at_lower_bound?: ObservedAtBound;
  observed_at_upper_bound?: ObservedAtBound;
  respect_soft_deletes?: boolean;
}

interface OfflineQueryMetaJson {
  resources?: {
    resource_group?: string;
    resourceGroup?: string;
  };
}

export const getOfflineQueryResourceGroup = (query: OfflineQueryMeta): string => {
  const explicitResourceGroup = query.resourceGroup?.trim();
  const queryMeta = maybeProtoValueToJson(query.queryMeta) as OfflineQueryMetaJson | undefined;
  const resourceGroup =
    explicitResourceGroup ||
    queryMeta?.resources?.resource_group?.trim() ||
    queryMeta?.resources?.resourceGroup?.trim();
  return resourceGroup && resourceGroup !== "default_resource_group" ? resourceGroup : "default";
};

export const OBSERVED_AT_BOUND_DELTA_PREFIX = "delta:";

// observed_at_lower_bound / observed_at_upper_bound are encoded as one of:
//   - an ISO8601 timestamp string (e.g. "2025-10-25T00:00:00+00:00")
//   - a "delta:<duration>" string (e.g. "delta:-368d") representing a timedelta relative to __ts__
//   - a JSON number of seconds (Pydantic v1 default for timedelta when filters are persisted via
//     OfflineStorageJobFilters.json()) — also a timedelta relative to __ts__
// See chalkshared.rpc_models.offline.OfflineStorageJobFilters and chalkshared.rpc_models.query.
export function isObservedAtBoundDelta(
  bound: ObservedAtBound | null | undefined,
): bound is number | string {
  if (bound == null) return false;
  if (typeof bound === "number") return true;
  return bound.startsWith(OBSERVED_AT_BOUND_DELTA_PREFIX);
}

// Convert a number of seconds to a chalk duration string (e.g. -31795200 -> "-368d").
// Mirrors chalk-rs/chalk-utils/src/duration.rs::seconds_to_duration_string.
export function secondsToChalkDuration(totalSeconds: number): string {
  if (totalSeconds === 0) return "0s";
  const negative = totalSeconds < 0;
  const abs = Math.abs(totalSeconds);
  const wholeSeconds = Math.trunc(abs);
  const milliseconds = Math.round((abs - wholeSeconds) * 1000);

  const days = Math.trunc(wholeSeconds / 86400);
  let remainder = wholeSeconds % 86400;
  const hours = Math.trunc(remainder / 3600);
  remainder = remainder % 3600;
  const minutes = Math.trunc(remainder / 60);
  const seconds = remainder % 60;

  let out = negative ? "-" : "";
  if (days > 0) out += `${days}d`;
  if (hours > 0) out += `${hours}h`;
  if (minutes > 0) out += `${minutes}m`;
  if (seconds > 0) out += `${seconds}s`;
  if (milliseconds > 0) out += `${milliseconds}ms`;
  return out;
}

// Render a delta-encoded bound as a "delta:<duration>" string. Accepts either the legacy
// string form (already prefixed) or a numeric seconds form.
export function formatObservedAtBoundDelta(bound: number | string): string {
  if (typeof bound === "number") {
    return OBSERVED_AT_BOUND_DELTA_PREFIX + secondsToChalkDuration(bound);
  }
  return bound;
}

export function getLogsTimeRangeForOfflineQueryShard(
  shard: OfflineQueryShard,
): DateRangeValueWithStart {
  // Start time: shard.createdAt (always set in practice)
  const start =
    shard.createdAt != null
      ? timestampToDate(shard.createdAt).toISOString()
      : new Date().toISOString();

  // End time: try shard.completedAt first
  let end: string | null = null;

  if (shard.completedAt != null) {
    end = timestampToDate(shard.completedAt).toISOString();
  } else {
    // Fall back to max completedAt from runs
    const runCompletedDates = (shard.runs || [])
      .map((run) => (run.completedAt != null ? timestampToDate(run.completedAt) : null))
      .filter((date): date is Date => date != null);

    if (runCompletedDates.length > 0) {
      // Find the maximum date by comparing timestamps
      const maxRunCompletedDate = runCompletedDates.reduce((max, current) =>
        current.getTime() > max.getTime() ? current : max,
      );
      end = maxRunCompletedDate.toISOString();
    } else {
      // Final fallback to "now"
      end = new Date().toISOString();
    }
  }

  return { start, end };
}

export function offlineQueryIsCancelableProto(query: OfflineQueryMeta) {
  return (
    query.status != null &&
    query.queryType != null &&
    offlineQueryStatusIsCancelableProto(query.status) &&
    offlineQueryTypeIsCancelableProto(query.queryType)
  );
}

function offlineQueryStatusIsCancelableProto(queryStatus: OfflineQueryStatus) {
  switch (queryStatus) {
    case OfflineQueryStatus.UNKNOWN:
    case OfflineQueryStatus.UNSPECIFIED:
    case OfflineQueryStatus.QUEUED:
    case OfflineQueryStatus.WORKING:
      return true;
    case OfflineQueryStatus.CANCELED:
    case OfflineQueryStatus.COMPLETED:
    case OfflineQueryStatus.FAILED:
      return false;
    default:
      compileOnlyAssertNever(queryStatus);
      return false;
  }
}

function offlineQueryTypeIsCancelableProto(queryType: OfflineQueryKind | undefined) {
  switch (queryType) {
    case OfflineQueryKind.UNSPECIFIED:
    case OfflineQueryKind.UNKNOWN:
    case OfflineQueryKind.ASYNC_OFFLINE_QUERY:
    case OfflineQueryKind.CRON_OFFLINE_QUERY:
    case OfflineQueryKind.DATASET_INGESTION:
    case OfflineQueryKind.AGGREGATION_BACKFILL:
      return true;
    case OfflineQueryKind.OFFLINE_QUERY:
    case OfflineQueryKind.TRAINING_JOB:
    case undefined:
      return false;
    default:
      compileOnlyAssertNever(queryType);
      return false;
  }
}

export function renderOfflineQueryTypeProto(queryType: OfflineQueryKind | undefined) {
  switch (queryType) {
    case OfflineQueryKind.ASYNC_OFFLINE_QUERY:
      return "Async";
    case OfflineQueryKind.CRON_OFFLINE_QUERY:
      return "Scheduled";
    case OfflineQueryKind.DATASET_INGESTION:
      return "Ingest";
    case OfflineQueryKind.AGGREGATION_BACKFILL:
      return "Agg. backfill";
    case OfflineQueryKind.TRAINING_JOB:
      return "Training";
    case OfflineQueryKind.OFFLINE_QUERY:
    case undefined:
      return "Standard";
    case OfflineQueryKind.UNKNOWN:
    case OfflineQueryKind.UNSPECIFIED:
      return "Unknown";
    default:
      compileOnlyAssertNever(queryType);
      return "Standard";
  }
}

/**
 * Every status the chip offers, derived from the enum rather than hand-listed so
 * a new OfflineQueryStatus member becomes a filter option with no edit here.
 * UNSPECIFIED and UNKNOWN are excluded: they are the proto's "not set" and
 * "unrecognized" sentinels, not states a user filters for.
 */
export const OFFLINE_QUERY_STATUS_FILTER_STATUSES: readonly OfflineQueryStatus[] = Object.values(
  OfflineQueryStatus,
).filter(
  (value): value is OfflineQueryStatus =>
    typeof value === "number" &&
    value !== OfflineQueryStatus.UNSPECIFIED &&
    value !== OfflineQueryStatus.UNKNOWN,
);

export const OFFLINE_QUERY_STATUS_FILTER_VALUES: readonly string[] =
  OFFLINE_QUERY_STATUS_FILTER_STATUSES.map((status) =>
    offlineQueryStatusTextProto(status, undefined),
  ).sort((a, b) => a.localeCompare(b));

// Type chip filter values are the unabbreviated forms of the Type column's
// cell labels (renderOfflineQueryTypeProto above); the menu has room for the
// full words that the cell lacks.
export const OFFLINE_QUERY_TYPE_FILTER_VALUES = [
  "Aggregation backfill",
  "Async",
  "Dataset ingest",
  "Scheduled",
  "Standard",
  "Training job",
] as const;

/**
 * Resolves a chip label back to its status. Both directions go through
 * offlineQueryStatusTextProto, so the label the chip shows and the status the
 * request carries cannot disagree — a switch on string literals could not be
 * checked against the enum at all.
 */
export const offlineQueryStatusFilterValueToProto = (
  value: string | undefined,
): OfflineQueryStatus | undefined =>
  value == null
    ? undefined
    : OFFLINE_QUERY_STATUS_FILTER_STATUSES.find(
        (status) => offlineQueryStatusTextProto(status, undefined) === value,
      );

/** Inverse of the above. Sentinel statuses have no chip option, so they map to
 *  undefined ("no filter") rather than to a label. */
export const offlineQueryStatusProtoToFilterValue = (
  status: OfflineQueryStatus | undefined,
): string | undefined =>
  status != null && OFFLINE_QUERY_STATUS_FILTER_STATUSES.includes(status)
    ? offlineQueryStatusTextProto(status, undefined)
    : undefined;

export const offlineQueryTypeFilterValueToProto = (
  value: string | undefined,
): OfflineQueryKind | undefined => {
  switch (value) {
    case "Aggregation backfill":
      return OfflineQueryKind.AGGREGATION_BACKFILL;
    case "Async":
      return OfflineQueryKind.ASYNC_OFFLINE_QUERY;
    case "Dataset ingest":
      return OfflineQueryKind.DATASET_INGESTION;
    case "Scheduled":
      return OfflineQueryKind.CRON_OFFLINE_QUERY;
    case "Standard":
      return OfflineQueryKind.OFFLINE_QUERY;
    case "Training job":
      return OfflineQueryKind.TRAINING_JOB;
    default:
      return undefined;
  }
};

export const offlineQueryTypeProtoToFilterValue = (
  type: OfflineQueryKind | undefined,
): string | undefined => {
  switch (type) {
    case OfflineQueryKind.AGGREGATION_BACKFILL:
      return "Aggregation backfill";
    case OfflineQueryKind.ASYNC_OFFLINE_QUERY:
      return "Async";
    case OfflineQueryKind.DATASET_INGESTION:
      return "Dataset ingest";
    case OfflineQueryKind.CRON_OFFLINE_QUERY:
      return "Scheduled";
    case OfflineQueryKind.OFFLINE_QUERY:
      return "Standard";
    case OfflineQueryKind.TRAINING_JOB:
      return "Training job";
    case OfflineQueryKind.UNKNOWN:
    case OfflineQueryKind.UNSPECIFIED:
    case undefined:
      return undefined;
    default:
      compileOnlyAssertNever(type);
      return undefined;
  }
};

/**
 * Computes the progress percentage for a batch progress from protobuf.
 */
export function computeBatchReportProgressProto(
  query: OfflineQueryMeta | null | undefined,
  progress: BatchProgress | null | undefined,
): number {
  try {
    if (!progress) {
      return query?.completedAt != null ? 100 : 0;
    }

    if (progress.end != null || progress.total === "0") {
      return 100;
    }

    const total = parseInt(progress.total, 10);
    const current = parseInt(progress.computed, 10);
    const failed = parseInt(progress.failed, 10);

    return Math.min((100 * (current + failed)) / total, 100);
  } catch {
    return 0;
  }
}

export interface SummaryObjectType {
  num_threads: number;
  performance_summary: {
    // other stuff
    pipelineStats: [];
  };
}

export function removePipelineStats(summary: SummaryObjectType) {
  return {
    ...summary,
    performance_summary: { ...summary.performance_summary, pipelineStats: "<omitted>" },
  };
}

export const reportHasValidProgressProto = (
  report: BatchReport | null | undefined,
): report is BatchReport & { progress: BatchProgress } => {
  if (!report?.progress?.total) {
    return false;
  }
  try {
    return parseInt(report.progress.total, 10) > 0;
  } catch {
    return false;
  }
};

export function batchReportStatusIsTerminalProto(status: BatchOpStatus): boolean {
  switch (status) {
    case BatchOpStatus.COMPUTE_ENDED:
    case BatchOpStatus.COMPLETED:
    case BatchOpStatus.FAILED:
      return true;
    case BatchOpStatus.COMPUTE_STARTED:
    case BatchOpStatus.UNSPECIFIED:
    case BatchOpStatus.INIT:
      return false;
    default:
      compileOnlyAssertNever(status);
      return false;
  }
}

export function isTerminalOfflineQueryStatus(
  status: OfflineQueryStatus | null | undefined,
): boolean {
  if (status == null) return false;

  switch (status) {
    case OfflineQueryStatus.CANCELED:
    case OfflineQueryStatus.COMPLETED:
    case OfflineQueryStatus.FAILED:
      return true;
    case OfflineQueryStatus.QUEUED:
    case OfflineQueryStatus.WORKING:
    case OfflineQueryStatus.UNKNOWN:
    case OfflineQueryStatus.UNSPECIFIED:
      return false;
    default:
      compileOnlyAssertNever(status);
      return false;
  }
}

export function offlineQueryHasTerminalStatusProto(
  offlineQuery: OfflineQueryMeta | null | undefined,
): boolean {
  return isTerminalOfflineQueryStatus(offlineQuery?.status);
}

export function offlineQueryNameForQueryTypeProto(queryType: OfflineQueryKind | undefined): string {
  switch (queryType) {
    case OfflineQueryKind.CRON_OFFLINE_QUERY:
      return "Scheduled query";
    case OfflineQueryKind.DATASET_INGESTION:
      return "Dataset ingest";
    case OfflineQueryKind.AGGREGATION_BACKFILL:
      return "Aggregation backfill";
    case OfflineQueryKind.TRAINING_JOB:
      return "Training job";
    case OfflineQueryKind.ASYNC_OFFLINE_QUERY:
    case OfflineQueryKind.OFFLINE_QUERY:
    case OfflineQueryKind.UNKNOWN:
    case OfflineQueryKind.UNSPECIFIED:
    case undefined:
      return "Offline query";
    default:
      compileOnlyAssertNever(queryType);
      return "Offline query";
  }
}

export function offlineQueryDocsHrefForQueryTypeProto(
  queryType: OfflineQueryKind | undefined,
): string {
  switch (queryType) {
    case OfflineQueryKind.CRON_OFFLINE_QUERY:
      return "https://docs.chalk.ai/docs/scheduled-query";
    case OfflineQueryKind.DATASET_INGESTION:
      return "https://docs.chalk.ai/docs/datasets";
    case OfflineQueryKind.AGGREGATION_BACKFILL:
      return "https://docs.chalk.ai/docs/materialized_aggregations";
    case OfflineQueryKind.TRAINING_JOB:
      return "https://docs.chalk.ai/docs/model_training";
    case OfflineQueryKind.ASYNC_OFFLINE_QUERY:
    case OfflineQueryKind.OFFLINE_QUERY:
    case OfflineQueryKind.UNKNOWN:
    case OfflineQueryKind.UNSPECIFIED:
    case undefined:
      return "https://docs.chalk.ai/docs/query-offline";
    default:
      compileOnlyAssertNever(queryType);
      return "https://docs.chalk.ai/docs/query-offline";
  }
}

export function offlineQueryDocsTopicForQueryTypeProto(
  queryType: OfflineQueryKind | undefined,
): string {
  switch (queryType) {
    case OfflineQueryKind.CRON_OFFLINE_QUERY:
      return "Scheduled queries";
    case OfflineQueryKind.DATASET_INGESTION:
      return "Dataset ingests";
    case OfflineQueryKind.AGGREGATION_BACKFILL:
      return "Aggregation backfills";
    case OfflineQueryKind.TRAINING_JOB:
      return "Training jobs";
    case OfflineQueryKind.ASYNC_OFFLINE_QUERY:
    case OfflineQueryKind.OFFLINE_QUERY:
    case OfflineQueryKind.UNKNOWN:
    case OfflineQueryKind.UNSPECIFIED:
    case undefined:
      return "Offline queries";
    default:
      compileOnlyAssertNever(queryType);
      return "Offline queries";
  }
}

export function offlineQueryIsTerminal(offlineQuery: OfflineQueryMeta) {
  return offlineQueryHasTerminalStatusProto(offlineQuery) && offlineQuery.completedAt != null;
}

/**
 * Returns the set of output feature FQNs for an offline query.
 *
 * Prefers `datasetRevision.outputFqns` which is populated once a dataset
 * revision exists, and falls back to the request-time `output` and
 * `required_output` arrays so that in-flight queries with no revision yet
 * still surface their requested outputs.
 */
export const getOfflineQueryOutputFqns = (query: OfflineQueryMeta): string[] => {
  const datasetOutputs = query.datasetRevision?.outputFqns;
  if (datasetOutputs != null && datasetOutputs.length > 0) {
    return datasetOutputs;
  }

  const collected: string[] = [];
  const collectStrings = (value: unknown) => {
    if (Array.isArray(value)) {
      for (const v of value) {
        if (typeof v === "string") {
          collected.push(v);
        }
      }
    }
  };

  collectStrings(maybeProtoValueToJson(query.output));
  collectStrings(maybeProtoValueToJson(query.requiredOutput));

  return uniq(collected);
};

/**
 * Distinct root namespaces of a query's output features (e.g. `order.amount` ->
 * `order`), in encounter order so the dominant namespace stays first. Skips
 * chalk-internal fqns and bare names with no `.` (not namespaced features).
 */
export const getOfflineQueryOutputNamespaces = (query: OfflineQueryMeta): string[] => {
  const namespaces = getOfflineQueryOutputFqns(query)
    .filter((fqn) => fqn.includes(".") && !fqnIsChalkInternal(fqn))
    .map(pathToFirstElement);
  return uniq(namespaces);
};

export type ExtendedShardStatusProto = OfflineQueryStatus | "Waiting" | "Retrying" | undefined;

export function getExtendedShardStatusProto(
  shard: OfflineQueryShard,
  jobQueueData?: {
    state: JobQueueState;
    attemptIdx: number;
  },
): ExtendedShardStatusProto {
  if (jobQueueData != null) {
    switch (jobQueueData.state) {
      case JobQueueState.WAITING:
        return "Waiting";
      case JobQueueState.SCHEDULED:
        return jobQueueData.attemptIdx <= 0 ? OfflineQueryStatus.QUEUED : "Retrying";
      case JobQueueState.RUNNING:
        return jobQueueData.attemptIdx <= 1 ? OfflineQueryStatus.WORKING : "Retrying";
      case JobQueueState.COMPLETED:
        return OfflineQueryStatus.COMPLETED;
      case JobQueueState.FAILED:
        return OfflineQueryStatus.FAILED;
      case JobQueueState.CANCELED:
        return OfflineQueryStatus.CANCELED;
      default:
        return undefined;
    }
  } else {
    return shard.status;
  }
}
